import {
  Banner,
  BrandMark,
  CloseButton,
  cx,
  IconTile,
  Kbd,
  Menu,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  Modal,
  StateSwap,
  type Area,
} from '@ims/ui'
import {
  AlertTriangle,
  BarChart3,
  BookOpenText,
  Boxes,
  CheckCircle2,
  ChefHat,
  ClipboardCheck,
  ClipboardList,
  Check,
  ChevronDown,
  CloudOff,
  CookingPot,
  FilePen,
  FileText,
  HandCoins,
  History,
  LayoutDashboard,
  Menu as MenuIcon,
  MoreHorizontal,
  Package,
  PackageCheck,
  PanelLeft,
  PicnicTable,
  Plus,
  RefreshCw,
  Search,
  Settings,
  ShoppingCart,
  Stamp,
  Hand,
  Truck,
  UserRound,
  Users,
  UtensilsCrossed,
} from '@ims/ui/icons'
import { useState, type ReactNode } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router'
import {
  useBusinessMode,
  useBusinessName,
  useEntitlements,
  useLive,
  useOnline,
  useSettings,
  useSyncState,
} from '../hooks.ts'
import { BusinessIcon } from '../lib/business-icon.tsx'
import { noRoomHint, useBusinessLimit } from '../lib/business-limit.ts'
import { dateLabel, todayIso } from '../lib/format.ts'
import { moveClick } from '../lib/motion.ts'
import { useNeedsLookCount } from '../lib/activity-live.ts'
import { GO_KEYS, useGlobalShortcuts } from '../lib/shortcuts.ts'
import { toggleSidebar, useSidebarCollapsed } from '../lib/sidebar.ts'
import { SUPPORT_EMAIL, SUPPORT_PHONE_LABEL, supportTel } from '../lib/support.ts'
import { trialDaysLeft } from '../lib/trial.ts'
import type { MemberRole } from '@ims/remote'
import { roleAtLeast, useCan, useRole, useWorkspace } from '../workspace.tsx'
import { AlertsBell, AlertsProvider, CountPill, useAlerts } from './alerts.tsx'
import { AppCommandPalette, type PageLink } from './command-palette.tsx'
import { usePurchaseAccess } from './sections.tsx'
import { StorageBanner, StorageInfoButton } from './storage.tsx'
import { AccountMenu } from './account-menu.tsx'
import { KitchenQueue } from './kitchen-queue.tsx'
import { HoldPlus } from './hold-plus.tsx'
import { NewOrder } from './new-order.tsx'
import { OfflineBanners, UnsyncedTracker } from './offline.tsx'
import { CreateBusinessDialog } from '../screens/onboarding.tsx'
import { SyncPanel } from './sync-panel.tsx'

const DASHBOARD: PageLink & { end?: boolean } = {
  to: '/',
  label: 'Dashboard',
  icon: LayoutDashboard,
  area: 'sell',
  end: true,
}
/** Who did what, and every alert (managers and up). Its count: today's things worth a look. */
const ACTIVITY_PATH = '/activity'
const PURCHASES_PATH = '/purchases'
/** Orders waiting for approval (docs/plans/po-approvals.md); shown where approval is in use. */
const APPROVALS_PATH = '/approvals'
/** Purchase requests (docs/plans/purchase-requests.md); with purchase_control. */
const REQUESTS_PATH = '/purchase-requests'
/** A restaurant's Orders page: one sidebar item for its three tabs. */
const SERVICE: PageLink = {
  to: '/orders',
  label: 'Orders',
  icon: UtensilsCrossed,
  area: 'kitchen',
  also: ['/tables', '/kitchen'],
}
/** The other two tabs, so search still finds them by name. */
const SERVICE_TABS: PageLink[] = [
  { to: '/tables', label: 'Tables', icon: PicnicTable, area: 'kitchen' },
  { to: '/kitchen', label: 'Kitchen', icon: ChefHat, area: 'kitchen' },
]
/** A shop's Sales: one sidebar item for its Invoices, Orders and Quotations tabs. */
const SALES: PageLink = {
  to: '/invoices',
  label: 'Sales',
  icon: FileText,
  area: 'sell',
  also: ['/orders', '/quotations'],
}
/**
 * Pages that became tabs of another item (docs/plans/nav-tabs.md). Not in the sidebar;
 * search still finds each by its old name and says where it lives now.
 */
const TAB_PAGES: (NavPage & { detail: string })[] = [
  {
    to: '/invoices',
    label: 'Invoices',
    icon: FileText,
    area: 'sell',
    shop: true,
    detail: 'Sales → Invoices',
  },
  {
    to: '/orders',
    label: 'Orders',
    icon: ClipboardCheck,
    area: 'sell',
    shop: true,
    noWaiter: true,
    detail: 'Sales → Orders',
  },
  {
    to: '/quotations',
    label: 'Quotations',
    icon: FilePen,
    area: 'sell',
    shop: true,
    noWaiter: true,
    detail: 'Sales → Quotations',
  },
  {
    to: '/purchase-orders?show=reorder',
    label: 'What to order',
    icon: ClipboardList,
    area: 'buy',
    noWaiter: true,
    detail: 'Purchase orders → What to order',
  },
  {
    to: REQUESTS_PATH,
    label: 'Purchase requests',
    icon: Hand,
    area: 'buy',
    min: 'staff',
    control: true,
    detail: 'Purchase orders → Requests',
  },
  {
    to: APPROVALS_PATH,
    label: 'Approvals',
    icon: Stamp,
    area: 'buy',
    min: 'manager',
    approvals: true,
    detail: 'Purchase orders → Approvals',
  },
  {
    to: PURCHASES_PATH,
    label: 'Purchases',
    icon: PackageCheck,
    area: 'buy',
    noWaiter: true,
    detail: 'Bills and payments → Received',
  },
  {
    to: '/payments-made',
    label: 'Payments made',
    icon: HandCoins,
    area: 'buy',
    noWaiter: true,
    detail: 'Bills and payments → Payments made',
  },
  {
    to: '/ingredients',
    label: 'Ingredients',
    icon: CookingPot,
    area: 'stock',
    restaurant: true,
    noWaiter: true,
    detail: 'Menu → Ingredients',
  },
]
/**
 * `min`: the lowest role that sees the page. `restaurant`: only in restaurant mode.
 * `shop`: never in restaurant mode (quotations would be mistaken for table orders).
 */
type NavPage = PageLink & {
  min?: MemberRole
  restaurant?: boolean
  shop?: boolean
  noWaiter?: boolean
  /** Only while the business uses approval (purchase_control and at least one step). */
  approvals?: boolean
  /** Only with purchase_control. */
  control?: boolean
}
const NAV_GROUPS: { label: string; items: NavPage[] }[] = [
  {
    label: 'Sell',
    items: [
      { ...SERVICE, restaurant: true },
      { to: '/bill', label: 'Quick bill', icon: ShoppingCart, area: 'sell' },
      { ...SALES, shop: true },
      { to: '/invoices', label: 'Invoices', icon: FileText, area: 'sell', restaurant: true },
      { to: '/customers', label: 'Customers', icon: Users, area: 'people' },
    ],
  },
  {
    label: 'Inventory',
    items: [
      { to: '/products', label: 'Products', icon: Package, area: 'stock', noWaiter: true },
      { to: '/stock', label: 'Stock', icon: Boxes, area: 'stock', noWaiter: true },
    ],
  },
  {
    label: 'Purchase',
    items: [
      {
        to: '/purchase-orders',
        label: 'Purchase orders',
        icon: ClipboardList,
        area: 'buy',
        noWaiter: true,
        also: [REQUESTS_PATH, APPROVALS_PATH],
      },
      {
        to: PURCHASES_PATH,
        label: 'Bills and payments',
        icon: PackageCheck,
        area: 'buy',
        noWaiter: true,
        also: ['/payments-made'],
      },
      { to: '/suppliers', label: 'Suppliers', icon: Truck, area: 'buy', noWaiter: true },
    ],
  },
  {
    label: 'Insights',
    items: [
      { to: '/reports', label: 'Reports', icon: BarChart3, area: 'insight', min: 'manager' },
      { to: ACTIVITY_PATH, label: 'Activity', icon: History, area: 'insight', min: 'manager' },
    ],
  },
]
const SETTINGS: PageLink = { to: '/settings', label: 'Settings', icon: Settings, area: 'neutral' }
// Not in the sidebar: it opens from the account menu, and the command palette finds it.
const ACCOUNT: PageLink = {
  to: '/account',
  label: 'Your account',
  icon: UserRound,
  area: 'neutral',
}

interface Nav {
  /** Waiters open on the Tables tab and have no dashboard. */
  home: (PageLink & { end?: boolean }) | null
  groups: { label: string; items: PageLink[] }[]
  /** Pages that are tabs of an item: found by search, not listed. */
  tabPages: PageLink[]
  restaurant: boolean
  /** Orders waiting for approval: part of the Purchase orders item's count. */
  approvalsWaiting: number
  /** Approved requests with something left to order: the rest of that count. */
  requestsToOrder: number
}

/**
 * The pages for this user's role and business. A shop sees what it always
 * did; restaurant mode adds the Orders page, and a restaurant calls its products the menu.
 */
function useNav(): Nav {
  const role = useRole()
  const { restaurant, menu } = useBusinessMode()
  // Orders waiting for approval, and whether approval is in use. One counting statement.
  const canApprove = roleAtLeast(role, 'manager')
  const { purchaseControl, approvals, approvalsWaiting, requestsToOrder } = usePurchaseAccess()
  const shown = (i: NavPage) =>
    roleAtLeast(role, i.min ?? 'viewer') &&
    (restaurant || !i.restaurant) &&
    !(restaurant && i.shop) &&
    !(i.noWaiter && role === 'waiter') &&
    (!i.approvals || (approvals && canApprove)) &&
    (!i.control || purchaseControl)
  const groups = NAV_GROUPS.map((g) => ({
    label: !restaurant
      ? g.label
      : g.label === 'Sell'
        ? 'Service'
        : g.label === 'Inventory' && menu
          ? 'Menu & stock'
          : g.label,
    items: g.items.filter(shown).map((i) =>
      // A restaurant's Products (or Menu) item holds the Ingredients tab.
      restaurant && i.to === '/products'
        ? { ...i, also: ['/ingredients'], ...(menu ? { label: 'Menu', icon: BookOpenText } : {}) }
        : i,
    ),
  })).filter((g) => g.items.length > 0)
  return {
    home: role === 'waiter' ? null : DASHBOARD,
    groups,
    tabPages: TAB_PAGES.filter(shown),
    restaurant,
    approvalsWaiting,
    requestsToOrder,
  }
}

function pagesFor(nav: Nav): PageLink[] {
  return [
    ...(nav.home ? [nav.home] : []),
    ...nav.groups.flatMap((g) => g.items),
    ...(nav.restaurant ? SERVICE_TABS : []),
    ...nav.tabPages,
    SETTINGS,
    ACCOUNT,
  ]
}

// The page you are on takes its area's tint. Spelled out so Tailwind sees every class.
const NAV_ACTIVE: Record<Area, string> = {
  sell: 'bg-sell-wash ring-sell-light',
  people: 'bg-people-wash ring-people-light',
  stock: 'bg-stock-wash ring-stock-light',
  buy: 'bg-buy-wash ring-buy-light',
  insight: 'bg-insight-wash ring-insight-light',
  kitchen: 'bg-kitchen-wash ring-kitchen-light',
  neutral: 'bg-neutral-wash ring-neutral-light',
  take: 'bg-take-wash ring-take-light',
}
const NAV_TEXT: Record<Area, string> = {
  sell: 'text-sell',
  people: 'text-people',
  stock: 'text-stock',
  buy: 'text-buy',
  insight: 'text-insight',
  kitchen: 'text-kitchen',
  neutral: 'text-neutral',
  take: 'text-take',
}

/** A product's page opened from Stock (`?from=stock`): Stock stays lit, not Products. */
function stockView(pathname: string, search: string): boolean {
  return pathname.startsWith('/products/') && new URLSearchParams(search).get('from') === 'stock'
}

function NavItem({
  to,
  label,
  icon: Icon,
  area = 'sell',
  also,
  end,
  onNavigate,
  badge = 0,
  collapsed = false,
}: PageLink & { end?: boolean; onNavigate?: () => void; badge?: number; collapsed?: boolean }) {
  const { pathname, search } = useLocation()
  const fromStock = stockView(pathname, search)
  const onTab =
    (also?.some((p) => pathname === p || pathname.startsWith(`${p}/`)) ?? false) ||
    (fromStock && to === '/stock')
  return (
    <NavLink
      to={to}
      end={end}
      onClick={(e) => {
        onNavigate?.()
        moveClick(to, 'section')(e)
      }}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        cx(
          // Presses in like a button (scale 0.97) while held; nothing moves after the click.
          'flex items-center rounded-xl py-1.5 text-sm font-medium transition-[color,background-color,scale] duration-150 ease-out active:scale-[0.97]',
          collapsed ? 'justify-center' : 'gap-2.5 px-2',
          (isActive && !(fromStock && to === '/products')) || onTab
            ? cx('font-semibold ring-1 ring-inset', NAV_ACTIVE[area], NAV_TEXT[area])
            : 'text-muted hover:bg-soft hover:text-ink',
        )
      }
    >
      {/* The rail keeps the label for screen readers and shows the count on the icon. */}
      <span className="relative shrink-0">
        <IconTile area={area} size="sm">
          <Icon className="size-[17px]" />
        </IconTile>
        {collapsed && <CountPill count={badge} className="absolute -top-2 -right-2.5" />}
      </span>
      <span className={collapsed ? 'sr-only' : 'flex-1'}>{label}</span>
      {!collapsed && <CountPill count={badge} />}
    </NavLink>
  )
}

/**
 * The top of the sidebar: the business you are in, and the way to another one
 * (or to a new one, for owners). Plain when there is nowhere else to go.
 */
function BusinessSwitcher({
  collapsed,
  onNavigate,
}: {
  collapsed: boolean
  /** The phone drawer closes after a choice. */
  onNavigate?: () => void
}) {
  const { current, memberships, selectTenant } = useWorkspace()
  const businessName = useBusinessName(current?.tenant.name)
  const { businessType } = useBusinessMode()
  const online = useOnline()
  const navigate = useNavigate()
  const canManage = useCan('manager')
  const [creating, setCreating] = useState(false)
  const owned = memberships.filter((m) => m.role === 'owner').length
  const isOwner = owned > 0
  // Why the account cannot own another, from the server; create_tenant decides anyway.
  const noRoom = noRoomHint(useBusinessLimit(owned, online))
  // Under the row's name, like a business's role: beside it the name wrapped.
  const closedWhy = !online ? 'Needs internet' : noRoom
  const icon = businessType ? (
    <span className="flex size-8 shrink-0 items-center justify-center rounded-xl bg-primary text-on-fill">
      <BusinessIcon type={businessType} className="size-[18px]" />
    </span>
  ) : (
    // The Sano Godam mark only until the business type is known.
    <BrandMark size={32} className="shrink-0 rounded-xl" />
  )
  const name = (
    <span className={collapsed ? 'sr-only' : 'min-w-0 flex-1 text-left'}>
      <span className="block truncate text-base font-bold leading-tight">{businessName}</span>
      <span className="block text-xs capitalize text-muted">{current?.role}</span>
    </span>
  )
  const frame = cx(
    'flex h-16 items-center gap-3 border-b border-border',
    collapsed ? 'justify-center' : 'px-3',
    // In the phone drawer the close button sits at the right of this row.
    onNavigate && 'pr-12',
  )
  if (memberships.length < 2 && !isOwner)
    return (
      <div className={cx(frame, !collapsed && 'px-4')}>
        {icon}
        {name}
      </div>
    )
  return (
    <div className={frame}>
      <Menu
        label={`Business: ${businessName}`}
        title={collapsed ? businessName : undefined}
        width={288}
        align="start"
        className={collapsed ? undefined : 'min-w-0 flex-1'}
        triggerClassName={cx(
          'flex items-center gap-3 rounded-xl p-1.5 transition-colors hover:bg-soft aria-expanded:bg-soft',
          collapsed ? '' : 'w-full min-w-0',
        )}
        trigger={
          <>
            {icon}
            {name}
            {!collapsed && <ChevronDown className="size-4 shrink-0 text-muted" />}
          </>
        }
      >
        <MenuLabel>Your businesses</MenuLabel>
        {memberships.map((m) => {
          const here = m.tenant_id === current?.tenant_id
          return (
            <MenuItem
              key={m.tenant_id}
              checked={here}
              onSelect={() => {
                onNavigate?.()
                selectTenant(m.tenant_id)
              }}
              icon={
                <span className="flex size-7 items-center justify-center rounded-lg bg-primary-light text-xs font-bold text-primary">
                  {(here ? businessName : m.tenant.name).charAt(0).toUpperCase()}
                </span>
              }
              hint={here ? <Check className="size-4 text-primary" /> : undefined}
            >
              <span className="block truncate">{here ? businessName : m.tenant.name}</span>
              <span className="block text-xs capitalize text-muted">{m.role}</span>
            </MenuItem>
          )
        })}
        {(canManage || isOwner) && <MenuSeparator />}
        {canManage && (
          <MenuItem
            icon={<Settings className="size-4" />}
            onSelect={() => {
              onNavigate?.()
              navigate('/settings?tab=business')
            }}
          >
            Business settings
          </MenuItem>
        )}
        {isOwner && (
          <MenuItem
            icon={<Plus className="size-4" />}
            disabled={!!closedWhy}
            onSelect={() => setCreating(true)}
          >
            <span className="block">Create a new business</span>
            {closedWhy && <span className="block text-xs text-muted">{closedWhy}</span>}
          </MenuItem>
        )}
      </Menu>
      <CreateBusinessDialog open={creating} onClose={() => setCreating(false)} />
    </div>
  )
}

function Sidebar({
  onNavigate,
  onSearch,
  collapsed = false,
}: {
  onNavigate?: () => void
  onSearch: () => void
  /** The icon rail (desktop only; the phone drawer is always full). */
  collapsed?: boolean
}) {
  const { home, groups, restaurant, approvalsWaiting, requestsToOrder } = useNav()
  const alerts = useAlerts()
  const needsLook = useNeedsLookCount(useCan('manager'))
  // Supplier bills past their due date. One counting statement: this runs on every screen.
  const canBuy = useCan('staff')
  const today = todayIso()
  const lateBills =
    useLive(
      (r) => (canBuy && r.payables ? r.payables.totals(today) : Promise.resolve(null)),
      [canBuy, today],
      ['supplier_bills', 'supplier_payments', 'supplier_credits', 'supplier_allocations'],
    ).data?.lateBills ?? 0
  return (
    <nav className="flex h-full flex-col">
      <BusinessSwitcher collapsed={collapsed} onNavigate={onNavigate} />
      {/* The list scrolls on short screens; the fade at the bottom says there is more. */}
      <div className="min-h-0 flex-1 overflow-y-auto px-2 pt-3 pb-5 [mask-image:linear-gradient(to_bottom,black_calc(100%-1.25rem),transparent)]">
        <button
          type="button"
          onClick={onSearch}
          title={collapsed ? 'Search (Ctrl K)' : undefined}
          className={cx(
            'mb-3 flex w-full items-center gap-2 rounded-xl border border-border bg-background py-2 text-sm text-muted transition-colors hover:border-primary/40 hover:text-ink',
            collapsed ? 'justify-center' : 'px-3',
          )}
        >
          <Search className="size-4 shrink-0" />
          <span
            className={collapsed ? 'sr-only' : 'flex-1 overflow-hidden text-left whitespace-nowrap'}
          >
            Search…
          </span>
          {!collapsed && <Kbd className="whitespace-nowrap">Ctrl K</Kbd>}
        </button>
        <div className="space-y-0.5">
          {home && <NavItem {...home} onNavigate={onNavigate} collapsed={collapsed} />}
          {groups.map((group) => (
            <div key={group.label} role="group" aria-label={group.label} className="space-y-0.5">
              {collapsed ? (
                <div aria-hidden className="mx-2 my-2 border-t border-border" />
              ) : (
                <div aria-hidden className="px-3 pb-1 pt-4 text-section text-subtle">
                  {group.label}
                </div>
              )}
              {group.items.map((item) => (
                <NavItem
                  key={item.to}
                  {...item}
                  onNavigate={onNavigate}
                  collapsed={collapsed}
                  badge={
                    restaurant && item.to === SERVICE.to
                      ? alerts.count
                      : item.to === ACTIVITY_PATH
                        ? needsLook
                        : item.to === PURCHASES_PATH
                          ? lateBills
                          : item.to === '/purchase-orders'
                            ? approvalsWaiting + requestsToOrder
                            : 0
                  }
                />
              ))}
            </div>
          ))}
        </div>
      </div>
      <div className="px-2 pb-3">
        <NavItem {...SETTINGS} onNavigate={onNavigate} collapsed={collapsed} />
      </div>
    </nav>
  )
}

/** Phones and tablets: the main pages one tap away, with Quick bill in the middle. */
function BottomNav({ onMore, nav }: { onMore: () => void; nav: Nav }) {
  const alerts = useAlerts()
  const { pathname, search } = useLocation()
  const fromStock = stockView(pathname, search)
  // A restaurant's Orders stays lit on its Tables and Kitchen tabs, a shop's Sales on its
  // Orders and Quotations tabs.
  const holder = nav.restaurant ? SERVICE : SALES
  const service = (to: string) => to === holder.to
  const item = (to: string, label: string, Icon: typeof Package, end = false) => (
    <NavLink
      to={to}
      end={end}
      onClick={moveClick(to, 'section')}
      className={({ isActive }) =>
        cx(
          'flex flex-col items-center gap-0.5 py-1 text-[11px] font-medium transition-[color,scale] duration-150 ease-out active:scale-[0.97]',
          (isActive && !(fromStock && to === '/products')) ||
            (service(to) &&
              holder.also!.some((p) => pathname === p || pathname.startsWith(`${p}/`)))
            ? 'text-primary'
            : 'text-muted',
        )
      }
    >
      <span className="relative">
        <Icon className="size-[22px]" />
        {nav.restaurant && to === SERVICE.to && (
          <CountPill count={alerts.count} className="absolute -top-1.5 -right-2.5" />
        )}
      </span>
      {label}
    </NavLink>
  )
  return (
    <nav
      aria-label="Quick links"
      className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-5 items-end border-t border-border bg-surface/95 px-2 pt-1.5 pb-[max(0.5rem,env(safe-area-inset-bottom))] backdrop-blur lg:hidden"
      // A layer of its own in a page change, drawn over the moving content (styles.css):
      // without it the fading page covered the bar for a moment on phones.
      style={{ viewTransitionName: 'bottom-bar' }}
    >
      {nav.home
        ? item('/', 'Home', LayoutDashboard, true)
        : item(SERVICE.to, 'Orders', UtensilsCrossed)}
      {item('/invoices', nav.restaurant ? 'Invoices' : 'Sales', FileText)}
      <HoldPlus />
      {nav.restaurant && nav.home
        ? item(SERVICE.to, 'Orders', UtensilsCrossed)
        : nav.home
          ? item('/products', 'Products', Package)
          : item('/customers', 'Customers', Users)}
      <button
        type="button"
        onClick={onMore}
        className="flex flex-col items-center gap-0.5 py-1 text-[11px] font-medium text-muted transition-[scale] duration-150 ease-out active:scale-[0.97]"
      >
        <MoreHorizontal className="size-[22px]" />
        More
      </button>
    </nav>
  )
}

function ShortcutsDialog({
  open,
  onClose,
  restaurant,
}: {
  open: boolean
  onClose: () => void
  restaurant: boolean
}) {
  const rows: [string, string][] = [
    ['Ctrl K or /', 'Search and jump anywhere'],
    ['Hold + (phones)', 'Start a customer, supplier, product… (tap + is a bill)'],
    ...GO_KEYS.filter((g) => (restaurant ? !g.shop : !g.restaurant)).map((g): [string, string] => [
      `G ${g.key.toUpperCase()}`,
      `Go to ${g.label}`,
    ]),
    ...(restaurant
      ? ([
          ['N', 'New order: dine in, takeaway or delivery'],
          ['↑ or ↓', 'Orders: move through the list'],
          ['P', 'Orders: print the selected check'],
          ['C', 'Orders: check out the selected order'],
        ] as [string, string][])
      : []),
    ['F2', 'Quick bill: focus the scanner'],
    ['F4', 'Quick bill: choose the customer'],
    ['F6', 'Quick bill: add a custom item'],
    ['F9', 'Quick bill: complete the sale'],
    ['[', 'Fold or open the sidebar'],
    ['?', 'Show this list'],
  ]
  return (
    <Modal open={open} onClose={onClose} title="Keyboard shortcuts" size="sm">
      <dl className="divide-y divide-border text-sm">
        {rows.map(([keys, what]) => (
          <div key={keys} className="flex items-center justify-between gap-4 py-2">
            <dt className="text-muted">{what}</dt>
            <dd className="flex shrink-0 gap-1">
              {keys.split(' or ').map((k) => (
                <Kbd key={k}>{k}</Kbd>
              ))}
            </dd>
          </div>
        ))}
      </dl>
    </Modal>
  )
}

function SyncIndicator() {
  const sync = useSyncState()
  const online = useOnline()
  const [open, setOpen] = useState(false)
  let icon: ReactNode
  let label: string
  let tone: 'success' | 'warning' | 'danger' | 'neutral' = 'success'
  if (sync.failed.length > 0) {
    icon = <AlertTriangle className="size-4" />
    label = `${sync.failed.length} issue${sync.failed.length === 1 ? '' : 's'}`
    tone = 'danger'
  } else if (!online || sync.phase === 'offline') {
    icon = <CloudOff className="size-4" />
    label = sync.pending ? `Offline · ${sync.pending} to sync` : 'Offline'
    tone = 'warning'
  } else if (sync.phase === 'pushing' || sync.phase === 'pulling') {
    icon = <RefreshCw className="size-4 animate-spin" />
    label = 'Syncing'
    tone = 'neutral'
  } else if (sync.phase === 'error') {
    icon = <AlertTriangle className="size-4" />
    label = 'Sync paused'
    tone = 'danger'
  } else if (sync.pending > 0) {
    icon = <RefreshCw className="size-4" />
    label = `${sync.pending} to sync`
    tone = 'neutral'
  } else {
    icon = <CheckCircle2 className="size-4" />
    label = 'Synced'
  }
  const toneClass = {
    success: 'text-success',
    warning: 'text-warning',
    danger: 'text-danger',
    neutral: 'text-muted',
  }[tone]
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={label}
        title={label}
        className={cx(
          'flex items-center gap-1.5 rounded-xl px-2.5 py-1.5 text-sm font-medium hover:bg-soft',
          toneClass,
        )}
      >
        {/* A new state (offline, syncing, synced, an issue) settles in over the last one;
            a count changing within one state does not. */}
        <StateSwap state={tone} className="items-center gap-1.5">
          {icon}
          <span className="hidden sm:inline">{label}</span>
        </StateSwap>
      </button>
      <SyncPanel open={open} onClose={() => setOpen(false)} />
    </>
  )
}

function Banners() {
  const settings = useSettings()
  const online = useOnline()
  const ent = useEntitlements()
  const now = new Date().toISOString()
  const readOnly = ent && (!ent.can_write || (ent.grace_until !== null && now > ent.grace_until))
  const inGrace =
    ent && !readOnly && ent.current_period_end !== null && now > ent.current_period_end
  const trialLeft = trialDaysLeft(ent, new Date())
  return (
    <>
      {!online && (
        <Banner tone="warning">
          <CloudOff className="mr-1 inline size-4" /> You’re offline. Billing and stock keep
          working; changes sync when you reconnect.
        </Banner>
      )}
      {readOnly && (
        <Banner tone="danger">
          The subscription is inactive, so this business is read-only. Renew to keep billing.
        </Banner>
      )}
      {inGrace && ent?.grace_until && (
        <Banner tone="warning">
          The subscription has ended. You can keep working until{' '}
          {dateLabel(ent.grace_until, settings)}; renew to avoid interruption.
        </Banner>
      )}
      {trialLeft !== null && ent?.current_period_end && (
        <Banner tone="warning">
          Your free trial ends{' '}
          {trialLeft === 0 ? 'today' : trialLeft === 1 ? 'tomorrow' : `in ${trialLeft} days`} (
          {dateLabel(ent.current_period_end, settings)}).{' '}
          <Link to="/settings?tab=subscription" className="font-medium underline">
            See plans
          </Link>
          , or email{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="font-medium underline">
            {SUPPORT_EMAIL}
          </a>{' '}
          or call{' '}
          <a href={supportTel} className="font-medium underline">
            {SUPPORT_PHONE_LABEL}
          </a>{' '}
          to subscribe.
        </Banner>
      )}
      <StorageBanner />
      <OfflineBanners />
      <UnsyncedTracker />
    </>
  )
}

export function AppShell() {
  return (
    <AlertsProvider>
      <Shell />
    </AlertsProvider>
  )
}

function Shell() {
  const [menuOpen, setMenuOpen] = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const nav = useNav()
  const collapsed = useSidebarCollapsed()
  // Quick bill (also as the quotation and order editor) and a table's order have their own bar at
  // the bottom on phones.
  const bottomNav =
    pathname !== '/bill' &&
    !/^\/tables\/./.test(pathname) &&
    !/^\/(quotations|orders)\/(new|[^/]+\/(edit|revise))$/.test(pathname)
  useGlobalShortcuts({
    palette: () => setPaletteOpen(true),
    help: () => setHelpOpen(true),
    sidebar: toggleSidebar,
    go: (path) => navigate(path),
  })
  const openPalette = () => {
    setMenuOpen(false)
    setPaletteOpen(true)
  }
  return (
    <div className="flex h-dvh overflow-hidden">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[70] focus:rounded-xl focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-on-fill"
      >
        Skip to content
      </a>
      <aside
        className={cx(
          'hidden shrink-0 border-r border-border bg-surface transition-[width] duration-150 lg:block',
          collapsed ? 'w-16' : 'w-60',
        )}
      >
        <Sidebar onSearch={openPalette} collapsed={collapsed} />
      </aside>
      {menuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/30" onClick={() => setMenuOpen(false)} />
          <aside className="relative h-full w-64 animate-fade-in overscroll-contain bg-surface shadow-modal">
            <CloseButton
              label="Close menu"
              onClick={() => setMenuOpen(false)}
              className="absolute right-3 top-3.5 z-10"
            />
            <Sidebar onNavigate={() => setMenuOpen(false)} onSearch={openPalette} />
          </aside>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-16 shrink-0 items-center justify-between gap-2 border-b border-border bg-surface px-4">
          <button
            type="button"
            aria-label="Open menu"
            onClick={() => setMenuOpen(true)}
            className="tap-target rounded-xl p-1.5 text-muted hover:bg-soft lg:hidden"
          >
            <MenuIcon className="size-5" />
          </button>
          <button
            type="button"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!collapsed}
            title={collapsed ? 'Expand sidebar ( [ )' : 'Collapse sidebar ( [ )'}
            onClick={toggleSidebar}
            className="hidden rounded-xl p-1.5 text-muted hover:bg-soft hover:text-ink lg:block"
          >
            <PanelLeft className="size-5" />
          </button>
          <KitchenQueue />
          <div className="flex-1" />
          <NewOrder />
          <button
            type="button"
            aria-label="Search"
            onClick={openPalette}
            className="tap-target rounded-xl p-1.5 text-muted hover:bg-soft lg:hidden"
          >
            <Search className="size-5" />
          </button>
          <AlertsBell />
          <div className="flex items-center">
            <SyncIndicator />
            <StorageInfoButton />
          </div>
          <AccountMenu onShortcuts={() => setHelpOpen(true)} />
        </header>
        <Banners />
        {/* relative: visually hidden inputs (absolute) must scroll with the page, or they
            stretch the document and the whole shell scrolls. */}
        {/* `page`: the only part a page change animates (styles.css, app-motion.md). */}
        <main
          id="main"
          tabIndex={-1}
          className="relative flex-1 overflow-y-auto focus:outline-none"
          style={{ viewTransitionName: 'page' }}
        >
          <div className={cx('mx-auto max-w-7xl p-4 sm:p-6', bottomNav && 'pb-28 lg:pb-6')}>
            <Outlet />
          </div>
        </main>
      </div>
      {bottomNav && <BottomNav onMore={() => setMenuOpen(true)} nav={nav} />}
      {paletteOpen && (
        <AppCommandPalette pages={pagesFor(nav)} onClose={() => setPaletteOpen(false)} />
      )}
      <ShortcutsDialog
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        restaurant={nav.restaurant}
      />
    </div>
  )
}
